import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { GameLength, HEARTS_BY_GAME_LENGTH } from '../additional-settings-modal/constants';
import { Analytics } from 'lib/AnalyticsLoader/analytics';
import { audioManager } from 'lib/audio/audio-manager';
import { AUDIO_TYPES } from 'lib/audio/audio.types';

import { ISelectOption } from 'shared/components/ui/Select/Select';
import { DEFAULT_BET, NULL_ADDRESS } from 'shared/constants/constants';
import { BetError } from 'shared/constants/shared-enums';
import { useAccount } from 'shared/hooks/use-web3';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useStartGameStore } from 'shared/store/start-game-store';
import { useUIStore } from 'shared/store/uiStore';
import { validateBet } from 'shared/utils/validateBet';

interface IUseCreateGameSectionProps {
  approveAllowanceHandler: (fullAllowance: boolean, bet: number) => void;
  startGameHandler: (bet: number, wins: number, playerB: string) => void;
}

export const useCreateGameSection = ({
  approveAllowanceHandler,
  startGameHandler,
}: IUseCreateGameSectionProps) => {
  const { address, chainId } = useAccount();

  const [bet, setBet] = useState(DEFAULT_BET);
  const [betIsValid, setBetIsValid] = useState(true);
  const [betError, setBetError] = useState<BetError | null>(null);
  const [fullAllowance, setFullAllowance] = useState(true);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isManualBet, setIsManualBet] = useState(false);

  const {
    balances,
    isLoadingBalances,
    setUserBet,
    currencyForSelect,
    selectedCurrency,
    setSelectedCurrency,
    userBets,
    availableCurrencies,
  } = useCurrenciesStore();

  const hasAnyBalanceOnChain = Object.values(balances).some((v) => v > 0);

  const selectedBalance = useMemo(
    () => selectedCurrency?.balance,
    [selectedCurrency, selectedCurrency?.balance, chainId],
  );

  useEffect(() => {
    if (bet && selectedCurrency) {
      const { valid, error } = validateBet(
        bet,
        selectedCurrency.symbol,
        selectedBalance,
        availableCurrencies,
      );
      setBetIsValid(valid);
      setBetError(error);
      if (error === BetError.LOW_BET) {
        Analytics.lowBetEvent(address, selectedBalance);
      } else if (error === BetError.LOW_BALANCE) {
        setInsufficientBalanceModalShow(true);
      }
    }
  }, [bet, selectedCurrency, selectedBalance]);

  const { setInsufficientBalanceModalShow } = useUIStore();

  const { currentAllowance, isFetching, isApproving, txHash, isStarting } = useStartGameStore();

  const modalOpenHandler = () => {
    setIsModalOpened(!isModalOpened);
  };

  useEffect(() => {
    if (!selectedCurrency || !chainId || isManualBet) return;

    const betValue = userBets?.[chainId]?.[selectedCurrency.symbol];
    const [minBet, defaultBet] = selectedCurrency.bet;

    let newBet: number;

    if (betValue && betValue > 0) {
      newBet = betValue;
    } else if (selectedCurrency.balance >= Number(defaultBet)) {
      newBet = Number(defaultBet);
    } else {
      newBet = Number(minBet);
    }

    setBet(String(newBet));
  }, [selectedCurrency, chainId, userBets, selectedCurrency, isManualBet]);

  useEffect(() => {
    setIsManualBet(false);
  }, [selectedCurrency]);

  useEffect(() => {
    if (isApproving) {
      audioManager.playSound(AUDIO_TYPES.CONFIRM_WALLET);
    }
  }, [isApproving]);

  useEffect(() => {
    if (bet && selectedCurrency && selectedCurrency.balance !== null) {
      const { valid, error } = validateBet(
        bet,
        selectedCurrency.symbol,
        selectedCurrency.balance,
        availableCurrencies,
      );
      setBetIsValid(valid);
      setBetError(error);
      if (error === BetError.LOW_BET) {
        Analytics.lowBetEvent(address, selectedCurrency.balance);
      } else if (error === BetError.LOW_BALANCE) {
        setInsufficientBalanceModalShow(true);
      }
    }
  }, [bet, selectedCurrency, availableCurrencies]);

  const handleBetChange = useCallback(
    (bet: string) => {
      setIsManualBet(true);

      let value = bet
        .replace(/[^0-9.,]/g, '')
        .replace(/,/g, '.')
        .replace(/(\..*)\./g, '$1')
        .replace(/^\./, '');

      if (value.startsWith('0') && value[1] && !['.', ','].includes(value[1])) {
        value = `0.${value.slice(1)}`;
      }

      if (chainId && selectedCurrency) {
        setUserBet(chainId, +value, selectedCurrency.symbol);
      }

      setBet(value);
    },
    [chainId, selectedCurrency, setUserBet],
  );

  const handleSetBetInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleBetChange(e.target.value);
    },
    [handleBetChange],
  );

  const handleSetBet = useCallback(
    (value: string) => {
      handleBetChange(value);
    },
    [handleBetChange],
  );

  const handleSelectCurrency = (currency: ISelectOption) => {
    setSelectedCurrency(currency.value as string);
  };

  const handleBetClick = useCallback(
    (value: string) => {
      audioManager.playSound(AUDIO_TYPES.CLICK);
      handleSetBet(value);
    },
    [handleSetBet],
  );

  const handleSwitchChange = useCallback((checked: boolean) => setFullAllowance(checked), []);

  const getAuthorizeDisable = useMemo(
    () =>
      !betIsValid ||
      Number(currentAllowance) >= +bet ||
      isApproving ||
      isFetching ||
      !bet ||
      selectedCurrency?.balance < Number(bet) ||
      selectedCurrency?.balance <= 0,
    [currentAllowance, bet, betIsValid, isApproving, isFetching, selectedCurrency],
  );
  const [searchParams] = useSearchParams();
  const handleStartGame = useCallback(() => {
    const winsString = searchParams.get('wins');
    const wins = HEARTS_BY_GAME_LENGTH[winsString as GameLength] || 3;
    const playerB = searchParams.get('invite') || NULL_ADDRESS;
    startGameHandler(Number(bet), wins, playerB);
  }, [bet, searchParams, startGameHandler]);

  const handleApprove = useCallback(
    () => approveAllowanceHandler(fullAllowance, +bet),
    [approveAllowanceHandler, fullAllowance, bet],
  );
  return {
    bet,
    selectedCurrency,
    selectedBalance,
    isApproving,
    getAuthorizeDisable,
    fullAllowance,
    betIsValid,
    currentAllowance,
    isFetching,
    isStarting,
    txHash,
    betError,
    currencyForSelect,
    isModalOpened,
    balances,
    hasAnyBalanceOnChain,
    isLoadingBalances,
    handleSetBet,
    handleSelectCurrency,
    handleBetClick,
    setFullAllowance,
    handleSetBetInput,
    modalOpenHandler,
    handleSwitchChange,
    handleStartGame,
    handleApprove,
  };
};
